import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Button,
  InputAdornment,
  Select,
  FormControl,
} from '@mui/material';
import PageHeader from '../../../components/PageHeader';
import StatCard from '../../dashboard/components/StatCard';
import LoadingLogo from '../../../components/LoadingLogo';
import {
  Description,
  Folder,
  VisibilityOutlined,
  FolderOpen,
  Search,
  FilterAltOff,
  Email,
  TableChart,
  Article,
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosConfig';
import { useAuth } from '../../../hooks/useAuth';

const DocumentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [retentions, setRetentions] = useState([]);
  const [retentionId, setRetentionId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    loadRetentions();
  }, []);

  useEffect(() => {
    setPage(0);
    loadDashboard(0);
  }, [startDate, endDate, retentionId]);

  useEffect(() => {
    loadDashboard(page);
  }, [page, rowsPerPage]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      loadDashboard(0, value);
    }, 400);
  };

  const loadRetentions = async () => {
    try {
      const params = user?.companyId ? { companyId: user.companyId, limit: 100 } : { limit: 100 };
      const res = await axiosInstance.get('/retentions', { params });
      setRetentions(res.data?.data || []);
    } catch {
      // ignore
    }
  };

  const loadDashboard = async (currentPage, searchOverride) => {
    try {
      setLoading(true);
      const search = searchOverride !== undefined ? searchOverride : searchTerm;
      const params = {
        page: (currentPage ?? page) + 1,
        limit: rowsPerPage,
      };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (retentionId) params.retentionId = retentionId;
      if (search) params.search = search;
      const res = await axiosInstance.get('/documents/dashboard', { params });
      setData(res.data?.data || null);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '\u2014';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const formatDate = (date) => {
    if (!date) return '\u2014';
    return new Date(date).toLocaleDateString('es-ES');
  };

  const handleNavigateToAssociation = (doc) => {
    if (doc.associationType === 'proceeding') {
      navigate(`/proceedings/${doc.association.id}`);
    } else if (doc.associationType === 'correspondence') {
      navigate(`/correspondences/${doc.association.id}`);
    }
  };

  const handleNavigateToRetentionLine = (retentionLineId) => {
    if (retentionLineId) {
      navigate(`/retentions/lines/${retentionLineId}`);
    }
  };

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setRetentionId('');
    setSearchTerm('');
    setPage(0);
    loadDashboard(0, '');
  };

  const displayDocuments = data?.documents || [];
  const totalPages = data?.pagination ? Math.ceil((data.pagination.total || 0) / rowsPerPage) : 1;
  const from = page * rowsPerPage + 1;

  return (
    <Box>
      <PageHeader
        title="Dashboard de Documentos"
        subtitle="Volumen documental, asociacion a expedientes y lineas de retencion."
        stats={data ? [
          { label: 'Total Documentos', value: data.totalDocuments?.toLocaleString() || 0 },
          { label: 'Total Expedientes', value: data.totalProceedings?.toLocaleString() || 0 },
          { label: 'Total Correspondencias', value: data.totalCorrespondences?.toLocaleString() || 0 },
        ] : undefined}
        actions={
          <Button startIcon={<FilterAltOff />} onClick={handleResetFilters} variant="outlined" size="small">
            Limpiar Filtros
          </Button>
        }
      />

      {/* Filters */}
      <Box sx={{ bgcolor: '#f2f2f3', p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Fecha Inicio</Typography>
            <TextField fullWidth size="small" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Fecha Fin</Typography>
            <TextField fullWidth size="small" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Retencion</Typography>
            <FormControl fullWidth size="small">
              <Select value={retentionId} onChange={(e) => setRetentionId(e.target.value)} displayEmpty>
                <MenuItem value="">Todas</MenuItem>
                {retentions.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar documentos</Typography>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Nombre, codigo..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : data ? (
        <>
          {/* Documents Table */}
          <Typography variant="h5" sx={{ mb: 2 }}>
            Documentos ({data?.pagination?.total || 0})
          </Typography>
          <Box>
            <Paper sx={{ overflow: 'hidden' }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Nombre</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Tamano</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Fecha</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Asociado a</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Linea de Retencion</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayDocuments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          No hay documentos para mostrar
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayDocuments.map((doc) => (
                        <TableRow key={doc.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                              {doc.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatFileSize(doc.fileSize)}</Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">{formatDate(doc.createdAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            {doc.associationType === 'proceeding' ? (
                              <Typography variant="body2" sx={{ cursor: 'pointer', color: '#5980a6' }} onClick={() => handleNavigateToAssociation(doc)}>
                                {doc.association.code}
                              </Typography>
                            ) : doc.associationType === 'correspondence' ? (
                              <Typography variant="body2" sx={{ cursor: 'pointer', color: '#5980a6' }} onClick={() => handleNavigateToAssociation(doc)}>
                                {doc.association.code || doc.association.title}
                              </Typography>
                            ) : (
                              <Chip label="Sin asociar" size="small" variant="outlined" sx={{ borderColor: 'rgba(29,31,32,0.16)' }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {doc.association?.retentionLine ? (
                              <Typography variant="body2" sx={{ cursor: 'pointer', color: '#5980a6' }} onClick={() => handleNavigateToRetentionLine(doc.association.retentionLineId)}>
                                {doc.association.retentionLine.series}
                              </Typography>
                            ) : (
                              <Typography variant="body2">&mdash;</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                              {doc.associationType && (
                                <Tooltip title={doc.associationType === 'proceeding' ? 'Ver Expediente' : 'Ver Correspondencia'}>
                                  <IconButton size="small" onClick={() => handleNavigateToAssociation(doc)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                    <VisibilityOutlined sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {doc.association?.retentionLineId && (
                                <Tooltip title="Ver Linea de Retencion">
                                  <IconButton size="small" onClick={() => handleNavigateToRetentionLine(doc.association.retentionLineId)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                    <TableChart sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {data?.pagination && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(29,31,32,0.55)' }}>
                  Mostrando {from} de {data.pagination.total || 0} documentos
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" disabled={page <= 0} onClick={() => setPage(page - 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
                    Anterior
                  </Button>
                  <Button variant="outlined" size="small" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
                    Siguiente
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default DocumentDashboard;
